"use client";

import React, { useEffect, useLayoutEffect, useRef } from "react";
import { Spinner, Text } from "ui-components";

import { useMessageGetConversationMessages } from "@/src/hooks/api/generated";
import { useChat } from "@/src/hooks/useChat";

import { Message } from "./Message";

export function ChatBoxConversationPanel(): JSX.Element {
  // Refs for DOM elements
  const lastMessageId = useRef<number | undefined | null>(null);
  const previousScrollHeight = useRef<number | undefined>();
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Custom hook for fetching messages
  const { mutate: getMessages, status: loadingNewMessagesStatus } =
    useMessageGetConversationMessages();

  // Custom hook for managing chat state
  const { messages, chatMetaData, setMessages, selectedConversation } =
    useChat();

  // Effect for detecting scroll and loading new messages
  useLayoutEffect((): (() => void) => {
    const container = containerRef.current;
    if (!container) {
      return () => {};
    }
    const handleScroll = (): void => {
      if (
        container.scrollTop < 100 &&
        loadingNewMessagesStatus !== "pending" &&
        messages &&
        messages.page &&
        messages?.data.length !== messages?.total
      ) {
        // If scrolled to top and not already loading new messages
        const newPage = messages.page + 1;
        getMessages(
          {
            data: {
              page: newPage,
              pageSize: 10,
              conversationId: selectedConversation?.conversationId,
            },
          },
          {
            onSuccess: (newMessages) => {
              // Reverse new messages and append to existing messages
              const reversedNewMessages = [...newMessages.data].reverse();
              if (reversedNewMessages.length > 0) {
                setMessages?.((prevMessages) => ({
                  ...newMessages,
                  data: [...reversedNewMessages, ...(prevMessages?.data ?? [])],
                  page: newMessages.page,
                }));
              }
            },
          }
        );
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [
    loadingNewMessagesStatus,
    messages,
    selectedConversation?.conversationId,
  ]);

  // Effect for scrolling to the end of messages
  useLayoutEffect(() => {
    const lastId = messages?.data?.[messages.data.length - 1]?.id;
    const container = containerRef.current;
    if (container) {
      if (lastMessageId.current !== lastId) {
        container.scrollTop = container.scrollHeight;
        previousScrollHeight.current = container.scrollHeight;
        lastMessageId.current = lastId;
        return;
      }
      if (previousScrollHeight.current) {
        container.scrollTop =
          container.scrollHeight - previousScrollHeight.current;
        previousScrollHeight.current = container.scrollHeight + 250;
      }
    }
  }, [messages]);

  useEffect(() => {
    previousScrollHeight.current = undefined;
    lastMessageId.current = undefined;
  }, [selectedConversation]);

  return (
    <div
      className="flex flex-1 flex-col gap-5 p-5 min-h-[500px] max-h-[500px] overflow-auto"
      ref={containerRef}
    >
      {/* Render message indicating all messages have been seen */}
      {messages?.data !== undefined &&
        messages?.data.length > 5 &&
        messages?.data.length === messages?.total && (
          <div className="flex justify-center items-center ">
            <Text variant="small">No more messages to show.</Text>
          </div>
        )}
      {/* Render loading spinner while loading new messages */}
      {loadingNewMessagesStatus === "pending" ? (
        <div className="flex justify-center items-center ">
          <Spinner intent="brand-primary" />
        </div>
      ) : null}
      {/* Render each message */}
      {chatMetaData.isSearchConnectionOpen
        ? null
        : messages?.data?.map((message) => (
            <Message message={message} key={message.id} />
          ))}
    </div>
  );
}
